<script lang="ts">
    // @ts-nocheck
    import tabIcon from "@/assets/vardplatsbestallning.svg";
    import BaseTab from "@/tabs/mixins/BaseTab.js";

    export default {
        // eslint-disable-next-line vue/multi-word-component-names
        name: "Receptforskrivningsmall",
        tabName: "Förskrivningsmall",
        extends: BaseTab,
        patientBound: false,
        icon: tabIcon,
        methods: {
            cancel(args?: { hasUnsavedData?: boolean } = {}) {
                this.closeTab(args.hasUnsavedData);
            },
        },
    };
</script>

<script setup lang="ts">
    import KForm from "@/components/forms/Form/KForm.vue";
    import { Trigger } from "@/components/forms/Form/types";
    import { useStore } from "@/store";
    import { convertToSubsetName } from "@/store/modules/helpers/baseSubset";
    import { ReceptforskrivningMall } from "@/store/modules/recept/receptforskrivningMallModule";
    import { ANNAT_VAL, capitalize, SilProductType } from "@/tabs/Receptforskrivning/helpers";
    import ReceptforskrivningMallFormPortal from "@/tabs/Receptforskrivning/Mall/Form/ReceptforskrivningMallFormPortal.vue";
    import BehandlingSection from "@/tabs/Receptforskrivning/Mall/Form/sections/BehandlingSection.vue";
    import DoseringSection from "@/tabs/Receptforskrivning/Mall/Form/sections/DoseringSection.vue";
    import ExpedieringsdetaljerSection from "@/tabs/Receptforskrivning/Mall/Form/sections/ExpedieringsdetaljerSection.vue";
    import LakemedelSection from "@/tabs/Receptforskrivning/Mall/Form/sections/LakemedelSection.vue";
    import NamnSection from "@/tabs/Receptforskrivning/Mall/Form/sections/NamnSection.vue";
    import { openDialog } from "@/utils";
    import { provide, ref, toRefs } from "vue";
    import emitter from "tiny-emitter/instance";

    const store = useStore();

    const props = withDefaults(
        defineProps<{
            /**
             * Id på tabben som renderar komponenten
             */
            tabId: number;
            /**
             * Id på mall, vid redigering
             */
            mallId?: string;
        }>(),
        {}
    );

    const { tabId, mallId } = toRefs(props);

    const formRef = ref();
    const expedieringsdetaljerSection = ref();
    const lakemedelSection = ref();
    const behandlingSection = ref();
    const doseringSection = ref();

    provide("formRef", formRef);

    defineExpose({
        hasUnsavedData: () => formRef?.value?.hasUnsavedData(),
    });

    /**
     * Callback vid emit accept från footern
     */
    const onAccept = async () => {
        const formData = await formRef?.value?.manualSubmit();

        if (!formData) {
            return;
        }

        try {
            const {
                namn,
                typ,
                forhandsvisning: doseringsanvisning,
                // START Ska inte skickas till BE.
                id: forskrivningId,
                sokLakemedel,
                visa_administrering,
                administrering_all,
                // END Ska inte skickas till BE
                ...payload
            } = formData;

            await store.dispatch(
                mallId?.value ? "receptforskrivningMall/updateMall" : "receptforskrivningMall/createMall",
                {
                    mall: {
                        namn,
                        typ,
                        id: mallId?.value ?? undefined,
                        forskrivning: {
                            items: [
                                {
                                    ...payload,
                                    doseringsanvisning,
                                },
                            ],
                        },
                    },
                }
            );

            store.dispatch("tabs/closeTab", tabId?.value);
        } catch (error) {
            openDialog("Kunde inte spara mallen", "error");
        }
    };

    /**
     * Callback vid emit cancel från footern
     */
    const onCancel = async () => {
        if (formRef?.value?.hasUnsavedData?.()) {
            emitter.emit("open-unsaved-dialog", { tabId: tabId?.value });

            return;
        }

        store.dispatch("tabs/closeTab", tabId?.value);
    };

    const getLakemedelSubsetName = () => {
        return convertToSubsetName({
            params: {
                nplPackId: formRef?.value?.getFieldValue({ name: "lakemedelId" }),
                nplId: formRef?.value?.getFieldValue({ name: "nplId" }),
            },
        });
    };

    const addPunctuation = ({ text }: { text: string | null }) => {
        if (!text) {
            return null;
        }

        return `${text}${text.endsWith(".") ? "" : "."}`;
    };

    const formTriggers: Trigger[] = [
        {
            fieldName: "tidsenhet",
            dependentOn: "tid_mellan_uttag",
            action: ({ value, dependentOnValue }) => {
                if (dependentOnValue) {
                    return {
                        value,
                        required: true,
                        disabled: false,
                    };
                }

                return {
                    value: null,
                    required: false,
                    disabled: true,
                };
            },
        },
        {
            fieldName: [
                "administreringsvag",
                "administreringsmetod",
                "administreringsstalle",
                "precisering_administreringsstalle",
                "medicinteknisk_produkt",
            ],
            dependentOn: "visa_administrering",
            action: ({ value, dependentOnValue }) => {
                const dependentOnValueTyped = dependentOnValue as string[];
                const valueTyped = value as { [key: string]: string };

                if (!dependentOnValue) {
                    return [];
                }

                return [
                    {
                        fieldName: "administreringsvag",
                        disabled: !dependentOnValueTyped.includes("vag"),
                        required: dependentOnValueTyped.includes("vag"),
                        value: dependentOnValueTyped.includes("vag") ? valueTyped.administreringsvag : null,
                    },
                    {
                        fieldName: "administreringsmetod",
                        disabled: !dependentOnValueTyped.includes("metod"),
                        required: dependentOnValueTyped.includes("metod"),
                        value: dependentOnValueTyped.includes("metod") ? valueTyped.administreringsmetod : null,
                    },
                    {
                        fieldName: "administreringsstalle",
                        disabled: !dependentOnValueTyped.includes("stalle"),
                        required: dependentOnValueTyped.includes("stalle"),
                        value: dependentOnValueTyped.includes("stalle") ? valueTyped.administreringsstalle : null,
                    },
                    {
                        fieldName: "precisering_administreringsstalle",
                        disabled: !dependentOnValueTyped.includes("stalle"),
                        required: false,
                        value: dependentOnValueTyped.includes("stalle")
                            ? valueTyped.precisering_administreringsstalle
                            : null,
                    },
                    {
                        fieldName: "medicinteknisk_produkt",
                        disabled: !dependentOnValueTyped.includes("medicinteknisk_produkt"),
                        required: dependentOnValueTyped.includes("medicinteknisk_produkt"),
                        value: dependentOnValueTyped.includes("medicinteknisk_produkt")
                            ? valueTyped.medicinteknisk_produkt
                            : null,
                    },
                ];
            },
        },
        {
            fieldName: ["forhandsvisning", "administreringsinstruktion"],
            dependentOn: [
                "behandlingsandamal",
                "dosering",
                // START Administration
                "administreringsmetod",
                "administreringsmetod_annan",
                "administreringsvag",
                "administreringsvag_annan",
                "administreringsstalle",
                "administreringsstalle_annan",
                "precisering_administreringsstalle",
                "precisering_administreringsstalle_annan",
                "medicinteknisk_produkt",
                "medicinteknisk_produkt_annan",
                // SLUT Administration
                "annan_instruktion",
            ],
            action: ({ dependentOnValue }) => {
                const dependentOnValueTyped = dependentOnValue as { [key: string]: string };

                const administreringsmetod = store.getters["nll/administreringsmetoderById"]({
                    id: dependentOnValueTyped?.administreringsmetod,
                });
                const administreringsvag = store.getters["nll/administreringsvagarById"]({
                    id: dependentOnValueTyped?.administreringsvag,
                });
                const administreringsstalle = store.getters["nll/administreringsstallenById"]({
                    id: dependentOnValueTyped?.administreringsstalle,
                });
                const precisering_administreringsstalle = store.getters["nll/preciseringarById"]({
                    id: dependentOnValueTyped?.precisering_administreringsstalle,
                });
                const medicinteknisk_produkt = store.getters["nll/medicintekniskaProdukterById"]({
                    id: dependentOnValueTyped?.medicinteknisk_produkt,
                });

                const content = [];
                // Det är i den här ordningen de ska skrivas ut. Don't move around inom arrayen
                const administreringContent = [
                    administreringsmetod?.patient_display ?? administreringsmetod?.label,
                    dependentOnValueTyped?.administreringsmetod_annan,
                    administreringsvag?.patient_display ?? administreringsvag?.label,
                    dependentOnValueTyped?.administreringsvag_annan,
                    administreringsstalle?.patient_display ?? administreringsstalle?.label,
                    dependentOnValueTyped?.administreringsstalle_annan,
                    precisering_administreringsstalle?.patient_display ?? precisering_administreringsstalle?.label,
                    dependentOnValueTyped?.precisering_administreringsstalle_annan,
                    medicinteknisk_produkt?.patient_display ?? medicinteknisk_produkt?.label,
                    dependentOnValueTyped?.medicinteknisk_produkt_annan,
                ].filter((text) => text);
                const capitalizedAdministreringContent = administreringContent
                    .map((text) => addPunctuation({ text: capitalize({ text }) }))
                    .join(" ");

                // Det är i den här ordningen de ska skrivas ut. Don't move around pushes
                content.push(
                    capitalize({
                        text: dependentOnValueTyped?.behandlingsandamal,
                    })
                );
                content.push(capitalize({ text: dependentOnValueTyped?.dosering }));
                content.push(capitalizedAdministreringContent);
                content.push(capitalize({ text: dependentOnValueTyped?.annan_instruktion }));

                const contentString = content
                    .filter((text) => text)
                    .map((text) => addPunctuation({ text }))
                    .join(" ");

                return [
                    {
                        fieldName: "forhandsvisning",
                        value: contentString.trim(),
                    },
                    {
                        fieldName: "administreringsinstruktion",
                        value: capitalizedAdministreringContent.trim(),
                    },
                ];
            },
        },
        {
            fieldName: "annan_orsak",
            dependentOn: "behandlingsorsak",
            action: ({ value, dependentOnValue }) => {
                if ((dependentOnValue as string) === ANNAT_VAL) {
                    return {
                        value,
                        disabled: false,
                        required: false,
                    };
                }

                return {
                    value: null,
                    disabled: true,
                    required: false,
                };
            },
        },
        {
            fieldName: "behandlingsandamal",
            dependentOn: "behandlingsorsak",
            action: ({ value, dependentOnValue, initial }) => {
                if (dependentOnValue && !initial) {
                    const behandlingsorsak = store.getters["nll/behandlingsorsakerById"]({
                        id: dependentOnValue as string,
                    });

                    if (behandlingsorsak?.patient_display) {
                        const trimmedDisplay = `${value ? `${value} ` : ""}${behandlingsorsak?.patient_display}`.trim();

                        if (trimmedDisplay.endsWith(".")) {
                            return { value: trimmedDisplay.slice(0, -1) };
                        }

                        return { value: trimmedDisplay };
                    }
                }

                return {
                    value,
                };
            },
        },
        {
            fieldName: [
                "administreringsmetod",
                "behandlingsorsak",
                "administreringsvag",
                "administreringsstalle",
                "precisering_administreringsstalle",
                "medicinteknisk_produkt",
            ],
            dependentOn: ["lakemedelId", "administrering_all"],
            action: async ({ value, dependentOnValue }) => {
                const valueTyped = value as { [key: string]: string };
                const dependentOnTyped = dependentOnValue as { administrering_all?: boolean; lakemedelId: string };

                /**
                 * lakemedelId sätts manuellt vid val av läkemedel, efter hämtning av data som ska vara med i options.
                 * Se LakemedelSection.vue
                 */

                if (!dependentOnTyped?.lakemedelId) {
                    return;
                }

                /**
                 * Gör en hämtning av kodverk oberoende av läkemdel om administrering_all är true och
                 * om metoder är tomt. Alla olika typer av kodverk hämtas samtidigt. Om metoder är tom är kodverk
                 * oberoende av läkemedel inte hämtade än
                 */

                if (
                    dependentOnTyped.administrering_all &&
                    !store.getters["nll/administreringsmetoderSubset"]({
                        subsetName: "default",
                    }).length
                ) {
                    await store.dispatch("nll/fetchKodverkByLakemedel");
                }

                const behandlingsorsakOptions = store.getters["nll/behandlingsorsakerSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const administreringsmetodOptions = store.getters["nll/administreringsmetoderSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const administreringsvagOptions = store.getters["nll/administreringsvagarSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const administreringsstalleOptions = store.getters["nll/administreringsstallenSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const preciseringOptions = store.getters["nll/preciseringarSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const medicintekniskProduktOptions = store.getters["nll/medicintekniskaProdukterSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });

                // value: Om administrering_all kryssas ur och värdet inte finns kvar som alternativ blir value null.
                return [
                    {
                        fieldName: "behandlingsorsak",
                        value: behandlingsorsakOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.behandlingsorsak)
                            ? valueTyped.behandlingsorsak
                            : null,
                        options: behandlingsorsakOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "administreringsmetod",
                        value: administreringsmetodOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.administreringsmetod)
                            ? valueTyped.administreringsmetod
                            : null,
                        options: administreringsmetodOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "administreringsvag",
                        value: administreringsvagOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.administreringsvag)
                            ? valueTyped.administreringsvag
                            : null,
                        options: administreringsvagOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "administreringsstalle",
                        value: administreringsstalleOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.administreringsstalle)
                            ? valueTyped.administreringsstalle
                            : null,
                        options: administreringsstalleOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "precisering_administreringsstalle",
                        value: preciseringOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.precisering_administreringsstalle)
                            ? valueTyped.precisering_administreringsstalle
                            : null,
                        options: preciseringOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "medicinteknisk_produkt",
                        value: medicintekniskProduktOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.medicinteknisk_produkt)
                            ? valueTyped.medicinteknisk_produkt
                            : null,
                        options: medicintekniskProduktOptions,
                        setOptionsDefault: true,
                    },
                ];
            },
        },
    ];

    const fetchFormData = async () => {
        if (mallId?.value) {
            const mall: ReceptforskrivningMall = store.getters["receptforskrivningMall/mallById"]({
                id: mallId.value,
            });

            const mallData = mall?.forskrivning?.items?.[0];

            if (!mallData) {
                return {};
            }

            const visa_administrering = [];

            if (mallData.administreringsmetod) {
                visa_administrering.push("metod");
            }

            if (mallData.administreringsvag) {
                visa_administrering.push("vag");
            }

            if (mallData.administreringsstalle) {
                visa_administrering.push("stalle");
            }

            if (mallData.medicinteknisk_produkt) {
                visa_administrering.push("medicinteknisk_produkt");
            }

            return {
                ...mallData,
                visa_administrering,
                forhandsvisning: mallData.doseringsanvisning,
                namn: mall.namn,
                typ: mall.typ,
                administrering_all: true,
                forpackningsinnehall: store.getters["lakemedel/lakemedelById"]({
                    id: mallData.lakemedelId,
                    displayMode: true,
                })?.forpackning,
            };
        }

        return {
            visa_administrering: [],
            silProductType: SilProductType.Approved,
            typ: "PRIVAT",
        };
    };
</script>

<template>
    <KForm
        :id="`receptforskrivning-mall-form-${tabId}`"
        class="receptforskrivning-mall-form"
        ref="formRef"
        :triggers="formTriggers"
        :initial-values-getter="fetchFormData"
        :listeners="[
            ...(expedieringsdetaljerSection?.formListeners ?? []),
            ...(lakemedelSection?.formListeners ?? []),
            ...(doseringSection?.formListeners ?? []),
            ...(behandlingSection?.formListeners ?? []),
        ]"
    >
        <template #header>
            <h2>{{ mallId ? "Redigera mall" : "Skapa mall" }}</h2>
        </template>
        <template #default>
            <NamnSection />
            <LakemedelSection ref="lakemedelSection" :mallId="mallId" />
            <ExpedieringsdetaljerSection ref="expedieringsdetaljerSection" :mallId="mallId" />
            <BehandlingSection ref="behandlingSection" />
            <DoseringSection ref="doseringSection" :mallId="mallId" />
            <ReceptforskrivningMallFormPortal
                :tabId="tabId"
                :mallId="mallId"
                formIdPrefix="receptforskrivning-mall-form-"
                @accept="onAccept"
                @cancel="onCancel"
            />
        </template>
    </KForm>
</template>

<style scoped lang="scss">
    .receptforskrivning-mall-form {
        // Behövs för att portal (footern) i tabben ska hamna längst ner
        flex-grow: 1;
    }
</style>
